
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UpdateFormData } from '../types';
import { useBaseMutation } from './baseMutation';

export const useCrudMutations = (resetForm: () => void, setIsDialogOpen: (open: boolean) => void) => {
  const { invalidateUpdates, showSuccessToast, showErrorToast } = useBaseMutation();

  const addUpdateMutation = useMutation({
    mutationFn: async (newUpdate: UpdateFormData) => {
      console.log('Adding new update:', newUpdate);
      const { data, error } = await supabase
        .from('automated_updates')
        .insert([{ ...newUpdate, is_active: true }])
        .select()
        .single();

      if (error) {
        console.error('Error adding update:', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      console.log('Update added successfully');
      invalidateUpdates();
      showSuccessToast("Success", "New automated update added successfully");
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      console.error('Add update error:', error);
      showErrorToast("Error", "Failed to add new update");
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number, data: UpdateFormData }) => {
      const { error } = await supabase
        .from('automated_updates')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      invalidateUpdates();
      showSuccessToast("Success", "Automated update modified successfully");
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error updating:', error);
      showErrorToast("Error", "Failed to update configuration");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const { error } = await supabase
        .from('automated_updates')
        .delete()
        .in('id', ids);

      if (error) throw error;
    },
    onSuccess: () => {
      invalidateUpdates();
      showSuccessToast("Success", "Selected updates deleted successfully");
    },
    onError: (error) => {
      console.error('Error deleting updates:', error);
      showErrorToast("Error", "Failed to delete updates");
    }
  });

  return {
    addUpdateMutation,
    updateMutation,
    deleteMutation
  };
};
